import React, { useContext, useState, useEffect } from "react"
import { db, storage } from "../firebase"
import firebase from "firebase/app";
import { useAuth } from "./AuthContext";
import { fetchUsers } from "../utils/api";
import { useAppContext } from "./AppContext";

const ENABLE_PERSISTENCE = false;

interface DataValue {
  db: firebase.firestore.Firestore | null,
  storage: firebase.storage.Storage | null,
  enabledPersistence: Boolean,
  rainfallLogsData: Array<any>,
  usersListData: Array<any>,
  gaugeData: Array<any>,
  riverScaleData: Array<any>,
  riverLevelLogsData: Array<any>,
  refreshRainfallData: () => void,
  refreshUserData: () => void,
  refreshGaugeData: () => void,
  refreshRiverScaleData: () => void,
  refreshRiverLevelData: () => void,
}

const DataContext = React.createContext<DataValue | null>(null);

export function useData() {
  return useContext(DataContext)
}

export const DataProvider: React.FC = ({ children }) => {
  const [enabledPersistence, setEnabledPersistence] = useState(ENABLE_PERSISTENCE);
  const [rainfallLogsData, setRainfallLogsData] = useState<Array<any>>([]);
  const [usersListData, setUsersListData] = useState<Array<any>>([])
  const [refreshUserFlag, setRefreshUserFlag] = useState(false);
  const [refreshRainfallFlag, setRefreshRainfallFlag] = useState(false);
  const [gaugeData, setGaugeData] = useState<Array<any>>([]);
  const [refreshGaugeDataFlag, setRefreshGaugeDataFlag] = useState(false);
  const [riverScaleData, setRiverScaleData] = useState<Array<any>>([]);
  const [refreshRiverScaleDataFlag, setRefreshRiverScaleDataFlag] = useState(false);
  const [riverLevelLogsData, setRiverLevelLogsData] = useState<Array<any>>([]);
  const [refreshRiverLevelFlag, setRefreshRiverLevelFlag] = useState(false);
  const auth = useAuth();
  const appContext = useAppContext();  

  useEffect(() => {
    if (ENABLE_PERSISTENCE) {
      (async () => {
        try {
          await db.enablePersistence({ synchronizeTabs: true });
        } catch (error) {
          setEnabledPersistence(false);
        }
      })();
    } else {
      setEnabledPersistence(false);
    }
    setRainfallLogsData([])
    setUsersListData([])
    setGaugeData([])
    setRiverScaleData([])
    setRiverLevelLogsData([])
  }, []);

  useEffect(() => {
    const query = db.doc("/app/HTmGIonlNNWrrBGbTmNX")
    const effect = query.onSnapshot((snapshot) => {
      let vData = snapshot.data()
      if(vData){
        appContext?.updateVersion(vData.version)
      }      
    })
    return effect
  }, [])

  /*Rainfall data query*/
  useEffect(() => {
    if ((auth?.currentUserRole && auth.currentUser) || (auth?.currentUserRole && auth.currentUser && refreshRainfallFlag)) {
      let query = null;
      if (auth.currentUserRole === 'admin')
        query = db.collection("Rainfall_data").orderBy('timestamp');
      else
        query = db.collection("Rainfall_data").where("uid", "==", auth?.currentUser?.uid!).orderBy('timestamp', 'desc');

      const effect = query.onSnapshot((snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            if (change.doc.data().Status !== -1) {
              let x = change.doc.data();
              setRainfallLogsData(arr => [...arr, {
                id: change.doc.id,
                docId: change.doc.id,
                ...x
              }])
            }
            //   console.log("Added: ", change.doc.data());
          }
          if (change.type === "modified") {
            setRainfallLogsData(arr => {
              let tempArr = [...arr];
              let index = tempArr.findIndex(item => item.timestamp === change.doc.data().timestamp && item.uid === change.doc.data().uid)
              //console.log('index', index);
              tempArr[index] = {
                id: change.doc.id,
                docId: change.doc.id,
                ...change.doc.data()
              }
              return tempArr
            })
            //  console.log("Modified: ", change.doc.data());
          }
          if (change.type === "removed") {
            setRainfallLogsData(arr => {
              let tempArr = [...arr];
              let index = tempArr.findIndex(item => item.timestamp === change.doc.data().timestamp && item.uid === change.doc.data().uid)
              // console.log('index', index);
              if (index > -1)
                tempArr.splice(index, 1);
              return tempArr
            })
            // console.log("Removed: ", change.doc.data());
          }
        });
      });
      setRefreshRainfallFlag(false)
      return effect;
    }
  }, [auth?.currentUserRole, auth?.currentUser, refreshRainfallFlag])

  /*Users list data*/
  useEffect(() => {
    if ((auth?.currentUserRole && auth.currentUser) || (auth?.currentUserRole && auth.currentUser && refreshUserFlag)) {
      if (auth?.currentUserRole === 'admin') {
        //console.log("Refreshing Data");

        const callApi = async () => {
          const token = await auth?.currentUser?.getIdToken();
          let response = null;
          if (token)
            response = await fetchUsers(token);

          if (response) {
            let resultArray = response.users;
            resultArray.forEach((item: any, i: number) => {
              item.id = i + 1;
            });
            setUsersListData(resultArray);
          }
        }
        callApi();
      }
    }
  }, [auth?.currentUserRole, auth?.currentUser, refreshUserFlag])

  useEffect(() => {
    if ((auth?.currentUserRole && auth.currentUser) || (auth?.currentUserRole && auth.currentUser && refreshGaugeDataFlag)) {
      let query = null;
      if (auth?.currentUserRole === 'admin')
        query = db.collection('RainGauges').orderBy('DateTime');
      else
        query = db.collection('RainGauges').where("Owner", "==", auth?.currentUser?.uid!).orderBy('DateTime', 'desc');
      const effect = query.onSnapshot((snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            let x = change.doc.data();
            setGaugeData(arr => [...arr, {
              id: change.doc.id,
              docId: change.doc.id,
              ...x
            }])
            //   console.log("Added: ", change.doc.data());
          }
          if (change.type === "modified") {
            setGaugeData(arr => {
              let tempArr = [...arr];
              let index = tempArr.findIndex(item => item.DateTime === change.doc.data().DateTime && item.uid === change.doc.data().uid)
              //console.log('index', index);
              tempArr[index] = {
                id: change.doc.id,
                docId: change.doc.id,
                ...change.doc.data()
              }
              return tempArr
            })
            //  console.log("Modified: ", change.doc.data());
          }
          if (change.type === "removed") {
            setGaugeData(arr => {
              let tempArr = [...arr];
              let index = tempArr.findIndex(item => item.DateTime === change.doc.data().DateTime && item.uid === change.doc.data().uid)
              // console.log('index', index);
              if (index > -1)
                tempArr.splice(index, 1);
              return tempArr
            })
            // console.log("Removed: ", change.doc.data());
          }
        });
      });
      setRefreshGaugeDataFlag(false)
      return effect;
    }
  }, [auth?.currentUserRole, auth?.currentUser, refreshGaugeDataFlag])

  useEffect(() => {
    if ((auth?.currentUserRole && auth.currentUser) || (auth?.currentUserRole && auth.currentUser && refreshRiverScaleDataFlag)) {
      let query = null;
      if (auth?.currentUserRole === 'admin')
        query = db.collection('RiverScales').orderBy('DateTime');
      else
        query = db.collection('RiverScales').where("Owner", "==", auth?.currentUser?.uid!).orderBy('DateTime', 'desc');
      const effect = query.onSnapshot((snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            let x = change.doc.data();
            setRiverScaleData(arr => [...arr, {
              id: change.doc.id,
              docId: change.doc.id,
              ...x
            }])
            //   console.log("Added: ", change.doc.data());
          }
          if (change.type === "modified") {
            setRiverScaleData(arr => {
              let tempArr = [...arr];
              let index = tempArr.findIndex(item => item.DateTime === change.doc.data().DateTime && item.uid === change.doc.data().uid)
              //console.log('index', index);
              tempArr[index] = {
                id: change.doc.id,
                docId: change.doc.id,
                ...change.doc.data()
              }
              return tempArr
            })
            //  console.log("Modified: ", change.doc.data());
          }
          if (change.type === "removed") {
            setRiverScaleData(arr => {
              let tempArr = [...arr];
              let index = tempArr.findIndex(item => item.DateTime === change.doc.data().DateTime && item.uid === change.doc.data().uid)
              // console.log('index', index);
              if (index > -1)
                tempArr.splice(index, 1);
              return tempArr
            })
            // console.log("Removed: ", change.doc.data());
          }
        });
      });
      setRefreshRiverScaleDataFlag(false)
      return effect;
    }
  }, [auth?.currentUserRole, auth?.currentUser, refreshRiverScaleDataFlag])

  useEffect(() => {
    if ((auth?.currentUserRole && auth.currentUser) || (auth?.currentUserRole && auth.currentUser && refreshRiverLevelFlag)) {
      let query = null;
      if (auth.currentUserRole === 'admin')
        query = db.collection("Riverlevel_data").orderBy('timestamp');
      else
        query = db.collection("Riverlevel_data").where("uid", "==", auth?.currentUser?.uid!).orderBy('timestamp', 'desc');

      const effect = query.onSnapshot((snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === "added") {
            if (change.doc.data().Status !== -1) {
              let x = change.doc.data();
              setRiverLevelLogsData(arr => [...arr, {
                id: change.doc.id,
                docId: change.doc.id,
                ...x
              }])
            }
            //   console.log("Added: ", change.doc.data());
          }
          if (change.type === "modified") {
            setRiverLevelLogsData(arr => {
              let tempArr = [...arr];
              let index = tempArr.findIndex(item => item.timestamp === change.doc.data().timestamp && item.uid === change.doc.data().uid)
              //console.log('index', index);
              tempArr[index] = {
                id: change.doc.id,
                docId: change.doc.id,
                ...change.doc.data()
              }
              return tempArr
            })
            //  console.log("Modified: ", change.doc.data());
          }
          if (change.type === "removed") {
            setRiverLevelLogsData(arr => {
              let tempArr = [...arr];
              let index = tempArr.findIndex(item => item.timestamp === change.doc.data().timestamp && item.uid === change.doc.data().uid)
              // console.log('index', index);
              if (index > -1)
                tempArr.splice(index, 1);
              return tempArr
            })
            // console.log("Removed: ", change.doc.data());
          }
        });
      });
      setRefreshRiverLevelFlag(false)
      return effect;
    }
  }, [auth?.currentUserRole, auth?.currentUser, refreshRiverLevelFlag])

  const refreshRainfallData = () => {
    setRainfallLogsData([])
    setRefreshRainfallFlag(true)
  }

  const refreshUserData = () => {
    setUsersListData([])
    setRefreshUserFlag(true)
  }

  const refreshGaugeData = () => {
    setGaugeData([])
    setRefreshGaugeDataFlag(true)
  }

  const refreshRiverScaleData = () => {
    setRiverScaleData([])
    setRefreshRiverScaleDataFlag(true)
  }

  const refreshRiverLevelData = () => {
    setRiverLevelLogsData([])
    setRefreshRiverLevelFlag(true);
  }

  const value = {
    db,
    storage,
    enabledPersistence,
    rainfallLogsData,
    usersListData,
    gaugeData,
    riverScaleData,
    riverLevelLogsData,
    refreshRainfallData,
    refreshUserData,
    refreshGaugeData,
    refreshRiverScaleData,
    refreshRiverLevelData
  }

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  )
}